/* SpawnAnything — hair & headgear library ("Frisuren und Kopfbedeckungen").
   recipe.hair = { style, color }   recipe.hat = { type, color, color2 }
   Hair is painted procedurally around the head box at the layer's voxel size (strand texture, volume, hairline);
   hats are a few primitives (head units, like `add` on 'head') or volume ops for shapes with openings.
   Recipes can still put free-form parts on top with `add`. Pure module (no three.js). */

export const HAIR_STYLES = ['keine', 'glatze', 'buerste', 'kurz', 'seitenscheitel', 'tolle', 'locken', 'afro', 'zopf', 'pferdeschwanz', 'irokese',
  'lang', 'dutt', 'wild', 'haarkranz', 'pilz', 'glatt', 'zoepfe', 'stachel', 'maehne'];
export const HAT_TYPES = ['keiner', 'helm_ritter', 'helm_roemisch', 'helm_griechisch', 'helm_stahl', 'helm_modern', 'helm_wikinger', 'pickelhaube',
  'helm_bau', 'helm_astronaut', 'helm_visier', 'krone', 'zylinder', 'kappe', 'muetze', 'barett', 'kopftuch', 'turban', 'nemes', 'cowboyhut',
  'dreispitz', 'zweispitz', 'mitra', 'hexenhut', 'kochmuetze', 'sombrero', 'stirnband', 'heiligenschein', 'fes', 'schirmmuetze', 'kapuze', 'ninjamaske', 'federhut'];

const hexOf = s => s === 'team' ? -1 : s === 'team2' ? -2 : typeof s === 'number' ? s : (typeof s === 'string' && /^#[0-9a-f]{6}$/i.test(s) ? parseInt(s.slice(1), 16) : null);
const rgb = c => [(c >> 16) & 255, (c >> 8) & 255, c & 255];
const pack = (r, g, b) => (Math.max(0, Math.min(255, Math.round(r))) << 16) | (Math.max(0, Math.min(255, Math.round(g))) << 8) | Math.max(0, Math.min(255, Math.round(b)));
const shade = (a, f) => { if (a < 0) return a; const A = rgb(a); return pack(A[0] * f, A[1] * f, A[2] * f); };
const mix = (a, b, t) => { if (a < 0 || b < 0) return t < 0.5 ? a : b; const A = rgb(a), B = rgb(b); return pack(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t); };
const toHex = c => c === -1 ? 'team' : c === -2 ? 'team2' : '#' + (c >>> 0).toString(16).padStart(6, '0');
const h3 = (x, y, z) => { let h = (x * 374761393 + y * 668265263 + z * 2147483647) | 0; h = (h ^ (h >>> 13)) * 1274126177; return ((h ^ (h >>> 16)) >>> 0) / 4294967296; };
const colorOr = (v, d) => { const c = typeof v === 'string' ? hexOf(v.toLowerCase()) : null; return c === null ? d : c; };

/* ---------------- hair ---------------- */
export function resolveHair(rec) {
  const h = rec.hair && typeof rec.hair === 'object' ? rec.hair : {};
  const style = HAIR_STYLES.includes(h.style) ? h.style : 'keine';
  return { style, color: colorOr(h.color, 0x3a2416) };
}

// rounded-box signed distance (metres) of a head-local point
function sdBox(px, py, pz, hx, hy, hz, r) {
  const qx = Math.abs(px) - hx + r, qy = Math.abs(py) - hy + r, qz = Math.abs(pz) - hz + r;
  const ox = Math.max(qx, 0), oy = Math.max(qy, 0), oz = Math.max(qz, 0);
  return Math.hypot(ox, oy, oz) + Math.min(Math.max(qx, qy, qz), 0) - r;
}
const ell = (u, v, w, c, r) => ((u - c[0]) / r[0]) ** 2 + ((v - c[1]) / r[1]) ** 2 + ((w - c[2]) / r[2]) ** 2;

/* Style definition: per head-local point decide hair / no hair. u, v, w in head units; d = signed distance to the
   head box (metres); t = layer voxel size (metres). Returns 0 = nothing, 1 = hair, 2 = hair (shadow tone), 3 = accent. */
function styleFn(style, H, hat) {
  const { hx, hy, hz } = H, unit = Math.min(hx, hy, hz);
  const top = hat ? 0.6 : 1; // hats squash the volume on top
  // hairline: lowest v that still has hair, per face sector
  const line = (u, w, f, s, b) => {
    if (w > 0.72 && w >= Math.abs(u) * 0.9) return f(u) - (Math.floor((u + 1) * 6.5) % 3 === 0 ? 0.09 : 0); // jagged fringe
    if (w < -0.72 && -w >= Math.abs(u) * 0.9) return b(u);
    // sides: front part (temple) .. back
    if (w > 0.35) return s.front; if (w > -0.35) return s.mid; return s.mid + (s.back - s.mid) * Math.min(1, (-w - 0.35) / 0.45);
  };
  const shell = (d, t, T, v, u, w, hl) => d > -t * 1.01 && d <= T && v >= hl;
  switch (style) {
    case 'buerste': return (u, v, w, d, t) => (d > -t * 1.01 && d <= 0.002 && v >= line(u, w, () => 0.55, { front: 0.3, mid: 0.2, back: -0.55 }, () => -0.55) ? 4 : 0);
    case 'kurz': return (u, v, w, d, t) => {
      const hl = line(u, w, () => 0.55, { front: 0.3, mid: 0.22, back: -0.6 }, () => -0.6);
      const T = (v > 0.85 ? 0.034 * top : 0.018) + (w > 0.75 && v > 0.5 ? 0.0 : 0);
      return shell(d, t, T, v, u, w, hl) ? 1 : 0;
    };
    case 'seitenscheitel': return (u, v, w, d, t) => {
      const hl = line(u, w, uu => 0.38 + 0.2 * (1 - uu) / 2, { front: 0.25, mid: 0.2, back: -0.6 }, () => -0.6);
      const T = v > 0.8 ? (0.04 + 0.016 * (u + 1) / 2) * top : 0.02;
      if (!shell(d, t, T, v, u, w, hl)) return 0;
      if (v > 0.95 && Math.abs(u - 0.42) < 0.07 && w > -0.5) return 2;
      return 1;
    };
    case 'tolle': return (u, v, w, d, t) => {
      const hl = line(u, w, () => 0.42, { front: 0.25, mid: 0.15, back: -0.55 }, () => -0.55);
      // big swoop: volume on top that rolls forward over the forehead, plus a lip hanging over the brow
      const blob = ell(u, v, w, [0.12, 0.95, 0.45], [1.08, 0.5 * top, 0.85]) <= 1 || ell(u, v, w, [0.25, 0.72, 1.02], [0.85, 0.2, 0.3]) <= 1;
      if (blob && d > -t && v > 0.35) return (v > 1.15 && w > 0.2) || (w > 1.05 && v > 0.75) ? 3 : (w > 1.15 && v < 0.62 ? 2 : 1);
      return shell(d, t, v > 0.8 ? 0.04 * top : 0.022, v, u, w, hl) ? (w > 0 && v > 0.9 ? 3 : 1) : 0;
    };
    case 'locken': return (u, v, w, d, t, x, y, z) => {
      const hl = line(u, w, () => 0.5, { front: 0.2, mid: 0.0, back: -0.75 }, () => -0.75);
      const bump = (h3(Math.floor(x * t / 0.033), Math.floor(y * t / 0.033), Math.floor(z * t / 0.033)) - 0.5) * 0.035;
      const T = (v > 0.8 ? 0.05 * top : 0.035) + bump;
      if (!shell(d, t, T, v, u, w, hl)) return 0;
      return d > T - t * 1.2 && bump > 0.006 ? 3 : bump < -0.01 ? 2 : 1;
    };
    case 'afro': return (u, v, w, d, t, x, y, z) => {
      const bump = (h3(Math.floor(x * t / 0.04), Math.floor(y * t / 0.04), Math.floor(z * t / 0.04)) - 0.5) * 0.12;
      const inside = ell(u, v, w, [0, 0.45, -0.12], [1.62, 1.42 * (hat ? 0.7 : 1), 1.55]) <= 1 + bump;
      if (!inside || d <= -t * 1.01) return 0;
      if (w > 0.7 && v < 0.5 && Math.abs(u) < 1.02) return 0; // face opening
      if (v < -0.35 && w > -0.3) return 0;
      return h3(Math.floor(x * t / 0.03), Math.floor(y * t / 0.03), Math.floor(z * t / 0.03)) > 0.75 ? 2 : 1;
    };
    case 'pilz': return (u, v, w, d, t) => {
      const hl = line(u, w, () => 0.3, { front: -0.15, mid: -0.15, back: -0.5 }, () => -0.5);
      return shell(d, t, v > 0.8 ? 0.045 * top : 0.04, v, u, w, hl) ? (v > 0.9 && w > 0 ? 3 : 1) : 0;
    };
    case 'glatt': return (u, v, w, d, t, x, y, z) => {
      const hl = line(u, w, () => 0.6, { front: 0.3, mid: 0.22, back: -0.6 }, () => -0.6);
      if (!shell(d, t, v > 0.8 ? 0.026 * top : 0.016, v, u, w, hl)) return 0;
      return v > 0.8 && (x & 1) === 0 ? 2 : (v > 0.9 && w > 0.3 && Math.abs(u) < 0.5 ? 3 : 1);
    };
    case 'irokese': return (u, v, w, d, t) => {
      if (Math.abs(u) > 0.22 || w < -1.05 || w > 0.95) return 0;
      const hgt = 0.35 + 0.35 * (1 - Math.abs(((w + 1) * 2.2) % 1 - 0.5) * 2) * (hat ? 0.3 : 1);
      if (d > -t * 1.01 && v > 0.5 && v <= 1 + hgt * (hy / unit) * 0.6) return v > 1 + hgt * 0.4 ? 3 : 1;
      return 0;
    };
    case 'lang': return (u, v, w, d, t, x) => {
      const hl = line(u, w, () => 0.55, { front: -9, mid: -9, back: -9 }, () => -9);
      // cap
      if (v > -1 && shell(d, t, v > 0.8 ? 0.03 * top : 0.03, v, u, w, hl)) {
        if (w > 0.72 && v < 0.55) return 0;
        return v > 0.95 && Math.abs(u) < 0.06 ? 2 : 1;
      }
      // front strands framing the face
      if (w > 0.95 && w <= 1 + 0.03 / hz && Math.abs(u) >= 0.74 && Math.abs(u) <= 1.04 && v <= 0.6 && v >= -1.3) return 1;
      // hanging curtain below the head: sides and back down to the shoulders
      const ox = (Math.abs(u) - 1) * hx, oz = (-w - 1) * hz;
      if (v <= -1 && v >= -2.25) {
        if (Math.abs(u) <= 1 + 0.03 / hx && Math.abs(u) > 1 - 0.02 && w < 0.7 && w > -1.05) return 1;
        if (w <= -1 + 0.02 && w >= -1 - 0.045 / hz && Math.abs(u) <= 1.04) return (x & 1) ? 2 : 1;
      }
      return 0;
    };
    case 'dutt': return (u, v, w, d, t) => {
      if (ell(u, v, w, [0, 1.05, -0.55], [0.42, 0.42 * top, 0.42]) <= 1) return 1;
      if (ell(u, v, w, [0, 0.88, -0.48], [0.44, 0.08, 0.44]) <= 1 && d > 0) return 3;
      const hl = line(u, w, () => 0.58, { front: 0.3, mid: 0.2, back: -0.55 }, () => -0.55);
      return shell(d, t, 0.018, v, u, w, hl) ? 1 : 0;
    };
    case 'wild': return (u, v, w, d, t, x, y, z) => {
      const n = (h3(Math.floor(x * t / 0.035), Math.floor(y * t / 0.035), Math.floor(z * t / 0.035)) - 0.5) * 0.35;
      if (d <= -t * 1.01) return 0;
      for (const s of [1, -1]) if (ell(u, v, w, [s * 1.0, 0.3, -0.25], [0.55, 0.72, 1.0]) <= 1 + n) return 1;
      if (ell(u, v, w, [0, 0.35, -1.0], [1.15, 0.75, 0.45]) <= 1 + n) return 1;
      if (v > 0.9 && w < 0.55 && h3(x, y >> 1, z) > 0.86 && d < 0.06) return 2; // wisps on the bald top
      return 0;
    };
    case 'haarkranz': return (u, v, w, d, t) => {
      if (v > 0.5 || v < -0.45 || w > 0.55) return 0;
      return d > -t * 1.01 && d <= 0.028 ? 1 : 0;
    };
    case 'zopf': case 'pferdeschwanz': case 'zoepfe': return (u, v, w, d, t, x, y, z) => {
      const hl = line(u, w, () => 0.55, { front: 0.3, mid: 0.15, back: -0.65 }, () => -0.65);
      if (shell(d, t, 0.02, v, u, w, hl)) return style === 'zoepfe' && v > 0.95 && Math.abs(u) < 0.06 ? 2 : 1;
      if (style === 'pferdeschwanz') {
        if (ell(u, v, w, [0, 0.55, -1.1], [0.2, 0.2, 0.15]) <= 1) return 3;
        for (let k = 0; k < 6; k++) { const s = k / 5; if (ell(u, v, w, [0, 0.4 - s * 2.0, -1.25 - Math.sin(s * 2) * 0.25], [0.3 - s * 0.12, 0.42, 0.28 - s * 0.08]) <= 1) return 1; }
        return 0;
      }
      const braid = (bx, bz, y0, n) => {
        for (let k = 0; k < n; k++) { const s = k / (n - 1), vv = y0 - k * 0.33; if (ell(u, v, w, [bx + (k % 2 ? 0.06 : -0.06), vv, bz], [0.2 - s * 0.05, 0.2, 0.18]) <= 1) return k === n - 1 ? 3 : (k % 2 ? 2 : 1); }
        return 0;
      };
      if (style === 'zopf') return braid(0, -1.12, -0.35, 7);
      for (const s of [1, -1]) { const r = braid(s * 1.12, -0.1, -0.15, 5); if (r) return r; }
      return 0;
    };
    case 'stachel': return (u, v, w, d, t, x, y, z) => {
      const hl = line(u, w, () => 0.5, { front: 0.25, mid: 0.2, back: -0.4 }, () => -0.4);
      if (shell(d, t, 0.02, v, u, w, hl)) return 1;
      // cones pointing up/out from a grid of roots on the top
      for (let i = -2; i <= 2; i++) for (let k = -2; k <= 1; k++) {
        const ru = i * 0.42, rw = k * 0.45 + 0.1, len = (0.55 + 0.2 * ((i + k) & 1)) * (hat ? 0.3 : 1);
        const dv = v - 1; if (dv < 0 || dv > len) continue;
        const rad = 0.22 * (1 - dv / len);
        if (Math.hypot(u - ru * (1 + dv * 0.3), w - rw * (1 + dv * 0.2)) <= rad) return dv > len * 0.7 ? 3 : 1;
      }
      return 0;
    };
    case 'maehne': return (u, v, w, d, t, x, y, z) => {
      const n = (h3(Math.floor(x * t / 0.04), Math.floor(y * t / 0.04), Math.floor(z * t / 0.04)) - 0.5) * 0.25;
      if (d <= -t * 1.01) return 0;
      if (w > 0.75 && Math.abs(u) < 0.95 && v < 0.95) return 0;
      if (ell(u, v, w, [0, 0.0, -0.25], [1.75, 1.7, 1.45]) > 1 + n) return 0;
      if (w > 0.95) return 0;
      return Math.abs(u) > 1.2 || v > 1.15 ? 2 : 1;
    };
  }
  return null;
}

/* Hair op for the head frame `fr` (metres). ctx: { head, headPart, kind } */
export function hairParts(rec, ctx) {
  const H = resolveHair(rec);
  const hat = rec.hat && typeof rec.hat === 'object' && HAT_TYPES.includes(rec.hat.type) && rec.hat.type !== 'keiner';
  if (H.style === 'keine') return null;
  const fr = ctx.head, hx = fr.h[0], hy = fr.h[1], hz = fr.h[2];
  const skin = ctx.skin;
  const base = H.color, dark = shade(base, 0.78), light = mix(base, 0xffffff, 0.22), sheen = mix(base, 0xffffff, 0.38);
  if (H.style === 'glatze') {
    return { bounds: [[-1, 0.9, -0.5], [1, 1.05, 1]], op: L => {
      const vs = L.vs, y = Math.floor((fr.c[1] + hy) / vs - 0.5);
      for (const [u, w] of [[-0.3, 0.45], [-0.15, 0.6], [-0.3, 0.6]]) {
        const x = Math.floor((fr.c[0] + u * hx) / vs), z = Math.floor((fr.c[2] + w * hz) / vs);
        const v = L.get(x, y, z); if (v && v.p === ctx.headPart) L.set(x, y, z, mix(v.c, 0xffffff, 0.35), ctx.headPart);
      }
    } };
  }
  const fn = styleFn(H.style, { hx, hy, hz }, hat);
  // under a hat the hair stops at the hat line (only the sides/back below the brim stay visible)
  const cut = Math.min(hat ? HAIR_CUT[rec.hat.type] ?? 9 : 9, ctx.hairCut ?? 9);
  if (!fn) return null;
  const B = { lang: [[-1.25, -2.3, -1.25], [1.25, 1.3, 1.1]], afro: [[-1.75, -0.6, -1.75], [1.75, 2.0, 1.1]], wild: [[-1.65, -0.5, -1.55], [1.65, 1.25, 0.9]],
    zopf: [[-1.1, -2.6, -1.45], [1.1, 1.2, 1.1]], pferdeschwanz: [[-1.1, -1.9, -1.9], [1.1, 1.2, 1.1]], zoepfe: [[-1.45, -1.75, -1.1], [1.45, 1.2, 1.1]],
    irokese: [[-0.3, 0.4, -1.15], [0.3, 1.9, 1.05]], tolle: [[-1.15, -0.7, -1.15], [1.25, 1.5, 1.3]], dutt: [[-1.1, -0.7, -1.1], [1.1, 1.55, 1.1]],
    stachel: [[-1.4, -0.5, -1.3], [1.4, 1.8, 1.2]], maehne: [[-1.85, -1.75, -1.75], [1.85, 1.75, 1.0]] }[H.style] || [[-1.25, -0.8, -1.25], [1.25, 1.3, 1.15]];
  const op = L => {
    const vs = L.vs;
    const lo = B[0].map((v, a) => Math.floor((fr.c[a] + v * fr.h[a]) / vs)), hi = B[1].map((v, a) => Math.floor((fr.c[a] + v * fr.h[a]) / vs));
    const r = Math.min(hx, hy, hz) * 0.18;
    for (let z = lo[2]; z <= hi[2]; z++) for (let y = Math.max(0, lo[1]); y <= hi[1]; y++) for (let x = lo[0]; x <= hi[0]; x++) {
      const px = (x + 0.5) * vs - fr.c[0], py = (y + 0.5) * vs - fr.c[1], pz = (z + 0.5) * vs - fr.c[2];
      const u = px / hx, v = py / hy, w = pz / hz;
      const d = sdBox(px, py, pz, hx, hy, hz, r);
      const cur = L.get(x, y, z);
      if (d <= -vs * 1.01 && !cur) continue;
      if (cur && cur.p !== ctx.headPart && d <= 0) continue; // never paint into other parts inside the head box
      if (v > cut) continue;
      const k = fn(u, v, w, d, vs, x, y, z);
      if (!k) continue;
      if (k === 4) { if (cur) L.set(x, y, z, (x + y + z) & 1 ? mix(skin, base, 0.65) : mix(skin, base, 0.4), ctx.headPart); continue; }
      if (cur && cur.p !== ctx.headPart) continue;
      // strand texture: darker every third strand (direction follows the sector), highlight bands on top
      let c = k === 2 ? dark : k === 3 ? light : base;
      if (k === 1) {
        // strands (every third one darker, along the combing direction), a soft sheen band on the crown
        const strand = Math.abs(u) > 0.92 && v < 0.9 ? z : x;
        if (((strand % 3) + 3) % 3 === 0) c = shade(base, 0.86);
        else if (v > 0.92 && w > -0.3 && w < 0.45 && u > -0.65 && u < -0.05) c = sheen;
      }
      L.set(x, y, z, c, ctx.headPart);
    }
  };
  return { op, bounds: B };
}

/* ---------------- headgear ---------------- */
// highest head-unit v where hair may show under each hat (closed helmets hide it all)
const HAIR_CUT = { helm_ritter: -1.2, helm_visier: -1.2, helm_astronaut: -1.6, helm_roemisch: 0.3, helm_griechisch: 0.3, helm_stahl: 0.35, helm_modern: 0.32,
  helm_wikinger: 0.3, pickelhaube: 0.32, helm_bau: 0.35, krone: 0.62, zylinder: 0.78, kappe: 0.45, muetze: 0.36, barett: 0.68, kopftuch: 0.33, turban: 0.38,
  nemes: -1.6, cowboyhut: 0.7, dreispitz: 0.72, zweispitz: 0.68, mitra: 0.72, hexenhut: 0.8, kochmuetze: 0.72, sombrero: 0.8, fes: 0.85, schirmmuetze: 0.62,
  kapuze: 0.62, ninjamaske: -1.6, federhut: 0.7 };
export function resolveHat(rec) {
  const h = rec.hat && typeof rec.hat === 'object' ? rec.hat : {};
  const type = HAT_TYPES.includes(h.type) ? h.type : 'keiner';
  const DEF = { helm_ritter: ['#9aa3ad', '#c6cdd5'], helm_roemisch: ['#9aa3ad', 'team'], helm_griechisch: ['#c8903a', 'team'], helm_stahl: ['#4f5d32', '#3e4a26'],
    helm_modern: ['#5a6040', '#2b2e33'], helm_wikinger: ['#7a828c', '#eadfc4'], pickelhaube: ['#2b2e33', '#d9b23c'], helm_bau: ['#f2c21c', '#d8a010'],
    helm_astronaut: ['#f4f4f2', '#3a3f47'], helm_visier: ['#3a3f47', '#3af0ff'], krone: ['#d9b23c', '#c8102e'], zylinder: ['#1a1a1f', '#7a1a22'],
    kappe: ['team', '#f4f4f2'], muetze: ['#c8322b', '#f4f4f2'], barett: ['#2a2a33', '#d9b23c'], kopftuch: ['#c8322b', '#f4f4f2'], turban: ['#f2ecd8', '#2a4fb0'],
    nemes: ['#d9b23c', '#2a4fb0'], cowboyhut: ['#8a5a33', '#3a2416'], dreispitz: ['#1a1a1f', '#d9b23c'], zweispitz: ['#1a1a1f', '#c8322b'], mitra: ['#f6f3ea', '#d9b23c'],
    hexenhut: ['#2a1a3a', '#7a3ab8'], kochmuetze: ['#f8f8f6', '#e2e2de'], sombrero: ['#d8b060', '#c8322b'], stirnband: ['team', 'team2'], heiligenschein: ['#ffe27a', '#ffffff'],
    fes: ['#b8202a', '#1a1a1f'], schirmmuetze: ['#3a4a32', '#d9b23c'], kapuze: ['#5a4a3a', '#3a2e22'], ninjamaske: ['#16161b', 'team'], federhut: ['#2a2a33', 'team'] }[type] || ['#888888', '#666666'];
  return { type, color: colorOr(h.color, hexOf(DEF[0])), color2: colorOr(h.color2, hexOf(DEF[1])) };
}
const E = (shape, at, size, color, o = {}) => ({ on: 'head', shape, at, size, color: toHex(color), ...o });
/* entries (head units) + volume ops for a hat; covers: whether the face is hidden (closed helmets) */
export function hatParts(rec, ctx) {
  const H = resolveHat(rec);
  if (H.type === 'keiner') return null;
  const c = H.color, c2 = H.color2, d = shade(c, 0.78), l = mix(c, 0xffffff, 0.25), out = [], ops = [];
  let bounds = [[-1.3, 0.2, -1.3], [1.3, 2.4, 1.3]];
  const S = (axis, col, period) => ({ stripes: { color: toHex(col), axis, period } });
  switch (H.type) {
    case 'helm_ritter':
      out.push(E('box', [0, 0.04, 0], [1.12, 1.1, 1.12], c), E('box', [0, 1.12, 0], [0.95, 0.08, 0.95], c2), E('box', [0, 0.42, 1.1], [1.1, 0.09, 0.06], d),
        E('box', [0.5, 0.15, 1.12], [0.4, 0.085, 0.05], 0x1c1f24, { mirror: true }), E('box', [0, -0.12, 1.16], [0.1, 1.0, 0.06], c2),
        E('box', [0.45, -0.45, 1.12], [0.26, 0.22, 0.05], c, { mirror: true, ...S('x', 0x1c1f24, 0.035) }), E('box', [0, -1.0, 0], [1.16, 0.1, 1.16], d));
      bounds = [[-1.2, -1.2, -1.2], [1.2, 1.3, 1.25]]; break;
    case 'helm_roemisch':
      out.push(E('box', [0, 0.62, -0.05], [1.12, 0.48, 1.12], c), E('box', [0, 0.42, 1.08], [1.12, 0.08, 0.12], 0xd9b23c), E('box', [0, 0.15, -1.05], [1.12, 0.6, 0.08], c),
        E('box', [0, -0.35, -1.18], [1.2, 0.08, 0.3], d), E('box', [0.99, -0.2, 0.45], [0.1, 0.6, 0.48], c, { mirror: true }), E('box', [1.06, -0.2, 0.45], [0.05, 0.42, 0.34], 0xd9b23c, { mirror: true }),
        E('box', [0, 1.13, 0], [0.16, 0.1, 0.8], 0xd9b23c), E('box', [0, 1.42, -0.05], [0.2, 0.32, 1.2], c2, S('z', shade(c2 < 0 ? 0x9a2420 : c2, 0.8), 0.066)));
      bounds = [[-1.25, -0.9, -1.5], [1.25, 1.8, 1.25]]; break;
    case 'helm_griechisch':
      out.push(E('box', [0, 0.62, -0.06], [1.12, 0.5, 1.12], c), E('box', [0, 0.0, -1.06], [1.12, 1.05, 0.08], c), E('box', [0.82, -0.25, 0.55], [0.3, 0.75, 0.6], c, { mirror: true }),
        E('box', [0, 0.32, 1.1], [0.14, 0.05, 0.05], d), E('box', [0, -0.05, 1.12], [0.09, 0.35, 0.07], c), E('box', [0, 0.42, 1.12], [1.1, 0.07, 0.06], d),
        E('box', [0, 1.05, -0.05], [0.24, 0.1, 0.9], d), E('box', [0, 1.4, -0.1], [0.2, 0.4, 1.28], c2), E('box', [0, 1.83, -0.1], [0.14, 0.06, 1.22], 0xf2ecd8));
      bounds = [[-1.25, -1.1, -1.5], [1.25, 1.95, 1.3]]; break;
    case 'helm_stahl':
      out.push(E('box', [0, 0.74, -0.03], [1.08, 0.3, 1.1], c), E('ellipsoid', [0, 0.62, -0.05], [1.2, 0.66, 1.22], c), E('box', [0, 0.4, -0.12], [1.28, 0.07, 1.32], d), E('box', [0, 0.28, -0.95], [1.2, 0.15, 0.4], c),
        E('box', [1.02, -0.25, 0.3], [0.05, 0.6, 0.07], 0x3a2e22, { mirror: true }));
      bounds = [[-1.35, -0.9, -1.45], [1.35, 1.35, 1.3]]; break;
    case 'helm_modern':
      out.push(E('box', [0, 0.74, -0.03], [1.08, 0.3, 1.1], c), E('ellipsoid', [0, 0.6, -0.05], [1.18, 0.62, 1.2], c, { checker: toHex(shade(c, 0.85)) }), E('box', [0, 0.35, -0.05], [1.2, 0.06, 1.22], d),
        E('box', [0, 0.95, 0.95], [0.25, 0.2, 0.12], 0x2b2e33), E('box', [0, 0.62, 0.0], [1.22, 0.07, 1.22], c2), E('box', [1.05, -0.2, 0.3], [0.05, 0.55, 0.07], 0x2b2e33, { mirror: true }));
      bounds = [[-1.35, -0.8, -1.35], [1.35, 1.3, 1.2]]; break;
    case 'helm_wikinger':
      out.push(E('box', [0, 0.74, -0.03], [1.08, 0.3, 1.1], c), E('ellipsoid', [0, 0.6, 0], [1.16, 0.72, 1.16], c), E('box', [0, 0.38, 0], [1.18, 0.08, 1.18], shade(c, 0.85)), E('box', [0, 0.95, 0], [0.08, 0.35, 1.18], shade(c, 0.85)),
        E('box', [0, 0.05, 1.12], [0.08, 0.38, 0.06], c), E('cone', [1.15, 1.1, 0], [0.16, 0.5, 0.16], c2, { mirror: true, rot: [0, 0, -40] }), E('cone', [1.55, 1.7, 0], [0.1, 0.25, 0.1], shade(c2, 0.85), { mirror: true, rot: [0, 0, -10] }));
      bounds = [[-1.9, -0.4, -1.25], [1.9, 2.0, 1.25]]; break;
    case 'pickelhaube':
      out.push(E('box', [0, 0.74, -0.03], [1.08, 0.3, 1.1], c), E('ellipsoid', [0, 0.6, -0.05], [1.12, 0.62, 1.14], c), E('box', [0, 0.32, 1.0], [0.9, 0.06, 0.25], c), E('box', [0, 0.65, 1.08], [0.35, 0.3, 0.05], c2),
        E('cone', [0, 1.45, -0.05], [0.14, 0.45, 0.14], c2), E('cylinder', [0, 1.1, -0.05], [0.22, 0.1, 0.22], c2));
      bounds = [[-1.25, -0.2, -1.25], [1.25, 1.95, 1.3]]; break;
    case 'helm_bau':
      out.push(E('box', [0, 0.74, -0.03], [1.08, 0.3, 1.1], c), E('ellipsoid', [0, 0.62, -0.05], [1.14, 0.62, 1.16], c), E('box', [0, 0.36, 0.15], [1.18, 0.05, 1.35], c), E('box', [0, 0.95, -0.05], [0.12, 0.3, 1.0], c2));
      bounds = [[-1.3, -0.2, -1.25], [1.3, 1.35, 1.55]]; break;
    case 'helm_astronaut':
      ops.push({ bounds: [[-1.4, -1.45, -1.4], [1.4, 1.45, 1.42]], fn: (u, v, w, dd) => {
        const r = Math.max(Math.abs(u), Math.abs(v) * 0.95, Math.abs(w));
        if (r > 1.38 || dd <= 0) return null;
        const win = w > 0.6 && Math.abs(u) < 0.82 && v > -0.85 && v < 0.62;
        if (win) { const rim = Math.abs(u) > 0.7 || v > 0.5 || v < -0.72; return rim && r > 1.2 ? c2 : null; }
        return r > 1.22 ? (v > 0.9 && w > 0.2 && Math.abs(u) < 0.4 ? mix(c, 0xffffff, 0.5) : c) : null;
      } });
      bounds = [[-1.45, -1.5, -1.45], [1.45, 1.5, 1.45]]; break;
    case 'helm_visier':
      out.push(E('box', [0, 0.1, -0.02], [1.14, 1.12, 1.14], c), E('box', [0, 0.12, 1.1], [1.0, 0.2, 0.08], 0x0e1014), E('box', [0, 0.12, 1.14], [0.85, 0.08, 0.05], c2, { glow: true }),
        E('box', [0, -0.55, 1.12], [0.42, 0.25, 0.06], shade(c, 0.7), S('y', shade(c, 0.5), 0.035)), E('box', [1.1, 0.0, 0], [0.08, 0.35, 0.35], shade(c, 0.85), { mirror: true }));
      bounds = [[-1.25, -1.15, -1.25], [1.25, 1.3, 1.25]]; break;
    case 'krone':
      out.push(E('box', [0, 0.82, 0], [1.08, 0.2, 1.08], c), E('box', [0, 0.68, 1.09], [0.9, 0.05, 0.04], shade(c, 0.8)));
      for (const [x, z] of [[0, 1], [0.75, 0.75], [-0.75, 0.75], [1, 0], [-1, 0], [0.75, -0.75], [-0.75, -0.75], [0, -1]]) out.push(E('cone', [x, 1.22, z], [0.18, 0.24, 0.18], c));
      out.push(E('box', [0, 0.84, 1.11], [0.12, 0.1, 0.04], c2), E('box', [0.55, 0.84, 1.11], [0.08, 0.08, 0.04], 0x2a6ad8, { mirror: true }));
      out.push(E('ellipsoid', [0, 0.95, 0], [0.98, 0.32, 0.98], c2));
      bounds = [[-1.25, 0.5, -1.25], [1.25, 1.55, 1.25]]; break;
    case 'zylinder':
      out.push(E('cylinder', [0, 0.92, 0], [1.38, 0.06, 1.38], c), E('cylinder', [0, 1.6, 0], [0.88, 0.68, 0.88], c), E('cylinder', [0, 1.05, 0], [0.9, 0.12, 0.9], c2),
        E('cylinder', [0, 2.27, 0], [0.88, 0.04, 0.88], l));
      bounds = [[-1.45, 0.7, -1.45], [1.45, 2.4, 1.45]]; break;
    case 'kappe':
      out.push(E('box', [0, 0.74, -0.03], [1.08, 0.3, 1.1], c), E('ellipsoid', [0, 0.72, -0.05], [1.13, 0.48, 1.13], c), E('box', [0, 0.52, 1.25], [0.78, 0.05, 0.42], c), E('cylinder', [0, 1.2, -0.05], [0.12, 0.05, 0.12], c2),
        E('box', [0, 0.85, 1.0], [0.3, 0.2, 0.08], c2));
      bounds = [[-1.25, 0.2, -1.25], [1.25, 1.3, 1.7]]; break;
    case 'muetze':
      out.push(E('box', [0, 0.74, -0.03], [1.08, 0.3, 1.1], c), E('ellipsoid', [0, 0.72, -0.05], [1.15, 0.58, 1.16], c, S('x', shade(c, 0.86), 0.035)), E('box', [0, 0.45, -0.02], [1.17, 0.15, 1.18], c2), E('ellipsoid', [0, 1.38, -0.05], [0.3, 0.26, 0.3], c2));
      bounds = [[-1.3, 0.1, -1.3], [1.3, 1.7, 1.3]]; break;
    case 'barett':
      out.push(E('ellipsoid', [0.18, 0.92, 0], [1.22, 0.24, 1.15], c, { rot: [0, 0, -12] }), E('box', [0, 0.72, 0], [1.08, 0.06, 1.08], shade(c, 0.8)), E('box', [-0.55, 0.85, 1.05], [0.14, 0.14, 0.05], c2));
      bounds = [[-1.3, 0.5, -1.3], [1.55, 1.3, 1.3]]; break;
    case 'kopftuch':
      out.push(E('box', [0, 0.72, -0.05], [1.1, 0.38, 1.12], c, { checker: toHex(c2) }), E('ellipsoid', [0.15, 0.62, 1.08], [0.9, 0.12, 0.06], c),
        E('ellipsoid', [0, 0.45, -1.18], [0.22, 0.2, 0.15], c), E('box', [0.18, 0.05, -1.25], [0.08, 0.38, 0.06], c, { mirror: true, rot: [0, 0, -15] }));
      bounds = [[-1.25, -0.4, -1.4], [1.25, 1.2, 1.25]]; break;
    case 'turban':
      out.push(E('ellipsoid', [0, 0.85, 0], [1.24, 0.6, 1.24], c, S('y', shade(c, 0.85), 0.05)), E('ellipsoid', [0, 1.25, -0.05], [0.8, 0.35, 0.8], c, S('x', shade(c, 0.85), 0.05)),
        E('box', [0, 0.8, 1.2], [0.14, 0.18, 0.06], c2), E('box', [0, 1.2, 1.05], [0.06, 0.3, 0.05], 0xf4f4f2));
      bounds = [[-1.35, 0.2, -1.35], [1.35, 1.7, 1.35]]; break;
    case 'nemes': {
      const st = S('y', c2, 0.066);
      out.push(E('box', [0, 0.78, -0.08], [1.2, 0.4, 1.12], c, st), E('box', [0, 0.1, -0.35], [1.2, 0.7, 0.8], c, st), E('box', [0, 0.6, 0.98], [1.15, 0.12, 0.1], c),
        E('box', [0.98, -0.5, 0.15], [0.2, 0.95, 0.45], c, { mirror: true, ...st }), E('box', [0, -0.5, -1.05], [0.5, 0.95, 0.12], c, st),
        E('box', [0, 0.85, 1.12], [0.12, 0.3, 0.1], c), E('ellipsoid', [0, 1.12, 1.15], [0.18, 0.15, 0.1], c2));
      bounds = [[-1.3, -1.5, -1.3], [1.3, 1.35, 1.3]]; break;
    }
    case 'cowboyhut':
      out.push(E('ellipsoid', [0, 0.78, 0], [1.75, 0.09, 1.62], c), E('box', [0, 1.12, 0], [0.82, 0.38, 0.88], c), E('box', [0, 1.5, 0], [0.1, 0.06, 0.7], d), E('box', [0, 0.9, 0], [0.86, 0.1, 0.92], c2));
      bounds = [[-1.8, 0.6, -1.7], [1.8, 1.6, 1.7]]; break;
    case 'dreispitz':
      out.push(E('cylinder', [0, 0.95, -0.05], [0.95, 0.35, 0.95], c), E('box', [0, 1.05, 0.85], [0.95, 0.32, 0.1], c, { rot: [-15, 0, 0] }),
        E('box', [0.95, 1.05, -0.4], [0.1, 0.32, 0.85], c, { mirror: true, rot: [0, 30, 0] }), E('box', [0, 1.32, 0.88], [0.95, 0.04, 0.08], c2, { rot: [-15, 0, 0] }));
      bounds = [[-1.3, 0.5, -1.3], [1.3, 1.6, 1.3]]; break;
    case 'zweispitz':
      out.push(E('ellipsoid', [0, 1.05, 0], [1.65, 0.55, 0.32], c), E('box', [0, 0.75, 0], [1.05, 0.12, 0.95], c), E('ellipsoid', [0.55, 1.15, 0.3], [0.16, 0.16, 0.06], c2), E('box', [0, 1.55, 0], [1.2, 0.04, 0.3], shade(c2, 1)));
      bounds = [[-1.75, 0.55, -1.0], [1.75, 1.65, 1.0]]; break;
    case 'mitra':
      out.push(E('box', [0, 1.3, -0.05], [0.82, 0.55, 0.8], c), E('box', [0, 1.95, -0.05], [0.55, 0.4, 0.55], c), E('box', [0, 2.42, -0.05], [0.22, 0.2, 0.22], c),
        E('box', [0, 0.82, -0.05], [0.86, 0.1, 0.84], c2), E('box', [0, 1.65, 0.78], [0.11, 0.6, 0.08], c2), E('box', [0, 1.85, 0.78], [0.38, 0.1, 0.08], c2));
      bounds = [[-1.0, 0.6, -1.0], [1.0, 2.7, 1.0]]; break;
    case 'hexenhut':
      out.push(E('cylinder', [0, 0.88, 0], [1.6, 0.06, 1.6], c), E('cone', [0, 1.65, -0.1], [0.95, 0.85, 0.95], c), E('cone', [0.25, 2.55, -0.35], [0.3, 0.4, 0.3], c, { rot: [-25, 0, -30] }),
        E('cylinder', [0, 1.05, 0], [0.98, 0.1, 0.98], c2));
      bounds = [[-1.7, 0.7, -1.7], [1.7, 3.0, 1.7]]; break;
    case 'kochmuetze':
      out.push(E('cylinder', [0, 0.95, 0], [1.05, 0.3, 1.05], c), E('ellipsoid', [0, 1.7, 0], [1.25, 0.65, 1.25], c, S('x', shade(c, 0.93), 0.08)));
      bounds = [[-1.35, 0.6, -1.35], [1.35, 2.4, 1.35]]; break;
    case 'sombrero':
      out.push(E('cylinder', [0, 0.85, 0], [2.3, 0.07, 2.3], c), E('cone', [0, 1.45, 0], [0.95, 0.65, 0.95], c), E('cylinder', [0, 1.0, 0], [1.0, 0.1, 1.0], c2),
        E('cylinder', [0, 0.92, 0], [2.3, 0.03, 2.3], c2, S('x', c, 0.1)));
      bounds = [[-2.35, 0.7, -2.35], [2.35, 2.15, 2.35]]; break;
    case 'stirnband':
      out.push(E('box', [0, 0.5, 0], [1.17, 0.11, 1.17], c), E('box', [0.18, 0.38, -1.25], [0.07, 0.3, 0.07], c, { mirror: true, rot: [20, 0, -15] }));
      bounds = [[-1.2, -0.1, -1.4], [1.2, 0.7, 1.2]]; break;
    case 'heiligenschein':
      for (let k = 0; k < 12; k++) { const a = k / 12 * Math.PI * 2; out.push(E('ellipsoid', [Math.cos(a) * 0.75, 1.55, Math.sin(a) * 0.75 - 0.1], [0.14, 0.06, 0.14], c, { glow: true })); }
      bounds = [[-1.0, 1.4, -1.0], [1.0, 1.7, 0.8]]; break;
    case 'fes':
      out.push(E('cylinder', [0, 1.1, 0], [0.72, 0.4, 0.72], c), E('cylinder', [0, 1.52, 0], [0.62, 0.03, 0.62], shade(c, 0.85)), E('box', [0.35, 1.25, 0.6], [0.04, 0.3, 0.04], c2, { rot: [0, 0, 10] }));
      bounds = [[-0.9, 0.6, -0.9], [0.9, 1.65, 0.9]]; break;
    case 'schirmmuetze':
      out.push(E('cylinder', [0, 1.05, -0.05], [1.25, 0.2, 1.22], c), E('cylinder', [0, 0.78, 0], [1.08, 0.12, 1.08], c2), E('box', [0, 0.62, 1.2], [0.75, 0.05, 0.32], 0x16161b, { rot: [12, 0, 0] }),
        E('box', [0, 1.02, 1.2], [0.18, 0.15, 0.05], 0xd9b23c));
      bounds = [[-1.35, 0.4, -1.35], [1.35, 1.35, 1.6]]; break;
    case 'kapuze':
      ops.push({ bounds: [[-1.4, -1.6, -1.6], [1.4, 1.5, 1.25]], fn: (u, v, w, dd) => {
        if (dd <= 0 || dd > 0.06) return null;
        if (w > 0.6 && Math.abs(u) < 0.86 && v < 0.72) return null;
        if (v < -1.05 && w > -0.2) return null;
        return v > 0.95 ? shade(c, 0.9) : c;
      } });
      bounds = [[-1.4, -1.6, -1.6], [1.4, 1.5, 1.25]]; break;
    case 'ninjamaske':
      ops.push({ bounds: [[-1.2, -1.2, -1.2], [1.2, 1.25, 1.2]], fn: (u, v, w, dd, cur) => {
        if (dd > 0.018) return null;
        if (dd <= -0.02) return null;
        if (w > 0.4 && v > -0.12 && v < 0.3) return null; // eye band stays open
        return cur ? c : (dd > 0 ? c : null);
      } });
      out.push(E('box', [0, 0.5, 0], [1.08, 0.09, 1.08], c2), E('box', [0.2, 0.42, -1.18], [0.06, 0.3, 0.06], c2, { mirror: true, rot: [20, 0, -15] }));
      bounds = [[-1.25, -1.25, -1.4], [1.25, 1.3, 1.25]]; break;
    case 'federhut':
      out.push(E('ellipsoid', [0, 0.82, 0], [1.6, 0.09, 1.5], c, { rot: [0, 0, 8] }), E('ellipsoid', [0, 1.1, -0.05], [0.9, 0.45, 0.9], c), E('box', [0, 0.95, 0], [0.92, 0.08, 0.92], 0xd9b23c),
        E('ellipsoid', [-0.7, 1.45, -0.4], [0.18, 0.7, 0.3], c2, { rot: [0, 0, 35] }));
      bounds = [[-1.7, 0.6, -1.6], [1.7, 2.1, 1.6]]; break;
  }
  return { entries: out, ops, bounds };
}
